<template>
  <cs-preloader-overlay :loading="loading">
    <div class="container-wrapper">
      <div class="container-content">
        <div class="header">
          <a class="header-back-link" :title="$t('common.back')" @click="$router.push({ name: 'cabinet' })"></a>
          <div class="header-title">
            {{ $t('transfer.check') }} <span>{{ transactionId }}</span>
          </div>
        </div>
        <div class="content">
          <div class="transfer-receipt">
            <div v-if="isOffline" class="transfer-receipt-offline">
              <receipt-self-employed :receipt="receipt" :qr-code="qrCode" />
            </div>
            <div v-else class="transfer-receipt-image">
              <img :src="`${receiptLink}?${new Date()}`" alt="" />
            </div>
            <div class="transfer-receipt-copy" @click="copyReceiptLink">
              <div class="transfer-receipt-copy-label">{{ $t('check.link') }}</div>
              <div class="transfer-receipt-copy-link">{{ receiptLink }}</div>
              <div class="transfer-receipt-copy-icon"></div>
            </div>
          </div>
          <div class="button-send">
            <button @click="onShownSharingDialog">
              {{ $t('to.share') }}
            </button>
          </div>
          <div v-if="isShownAnnulledButton" class="button-link-container">
            <div class="button-link" @click="handleAnnulledDeclarationShown">
              {{ $t('common.annul') }}
            </div>
          </div>
        </div>
      </div>

      <annulled-declaration-modal
        v-model="isAnnulledDeclarationShown"
        :transaction-id="transactionId"
        :receipt-cancel-reasons="receiptCancelReasonsList"
        @receipt-cancel="handleReceiptCancelByCode"
      />

      <cs-dialog v-model="isShownReceiptSharing">
        <dialog-default-layout>
          <h2 slot="title">{{ $t('share.on.social.media') }}</h2>
          <div slot="content">
            <div class="social-share-conatiner">
              <socials-share
                :url="receiptLink"
                :title="`${$t('your.check')} | capusta.space`"
                :description="PROJECT_INFO.description"
              />
            </div>
          </div>
        </dialog-default-layout>
      </cs-dialog>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

import logrocket from '@/services/logrocket';

import DialogDefaultLayout from 'modules/Dialogs/DefaultLayout';

import SocialsShare from '@/components/otherElements/SocialsShare';

import ReceiptSelfEmployed from './components/ReceiptSelfEmployed';
import AnnulledDeclarationModal from './components/AnnulledDeclarationModal';

import { ICON_BY_CODE } from './const';

export default {
  name: 'TransactionDeclaration',
  components: {
    DialogDefaultLayout,
    SocialsShare,
    ReceiptSelfEmployed,
    AnnulledDeclarationModal,
  },
  created() {
    this.loadData();
  },
  data() {
    return {
      receipt: {},
      qrCode: '',
      loading: false,
      isAnnulledDeclarationShown: false,
      receiptCancelReasonsList: [],
      isShownReceiptSharing: false,
    };
  },
  computed: {
    transactionId() {
      return this.$route.params.publicId;
    },
    isShownAnnulledButton() {
      if (this.$validate.isEmptyArray(this.receiptCancelReasonsList)) {
        return false;
      }

      return this.isSelfEmployedActivated && !this.isOffline && this.receipt.status === 'PROCESSED';
    },
    isOffline() {
      const { status } = this.receipt;

      return this.$validate.isNotUndefined(status) && status === 'NEW';
    },
    receiptLink() {
      if (!this.$validate.isObjectWithFields(this.receipt, ['receiptLink'])) {
        return '';
      }

      return this.receipt.receiptLink;
    },
    ...mapState(['PROJECT_INFO']),
    ...mapGetters(['isSelfEmployedActivated']),
  },
  methods: {
    async loadData() {
      this.loading = true;

      await this.loadDeclarationReceipt();
      await this.loadQRCodeToReceipt();
      await this.loadReceiptCancelReasons();

      this.loading = false;
    },
    async loadDeclarationReceipt() {
      try {
        const { data } = await this.DECLARATION_RECEIPT(this.transactionId);
        this.receipt = data;
      } catch (error) {
        logrocket.logger(error);
      }
    },
    async loadQRCodeToReceipt() {
      try {
        const { data } = await this.GET_SELF_EMPLOYED_QR_CODE(this.transactionId);

        const arrayBufferView = new Uint8Array(data);
        const blob = new Blob([arrayBufferView], { type: 'image/jpeg' });

        const urlCreator = window.URL || window.webkitURL;
        const imageUrl = urlCreator.createObjectURL(blob);

        this.qrCode = imageUrl;
      } catch (error) {
        logrocket.logger(error);
      }
    },
    async loadReceiptCancelReasons() {
      if (!this.isSelfEmployedActivated) {
        return;
      }

      try {
        const { data } = await this.GET_RECEIPT_CANCEL_REASONS();
        this.prepareReceiptCancelReasons(data.codes);
      } catch (error) {
        logrocket.logger(error);

        this.$alert({
          type: 'warning',
          text: this.$t('connection.to.fns.failed'),
        });
      }
    },
    prepareReceiptCancelReasons(list) {
      this.receiptCancelReasonsList = list.map(({ code, description }) => {
        return {
          code,
          description: this.$t(description),
          icon: ICON_BY_CODE[code],
        };
      });
    },
    copyReceiptLink() {
      this.$copyText(this.receiptLink);

      this.$alert({
        type: 'ok',
        text: this.$t('check.link.copied'),
      });
    },
    onShownSharingDialog() {
      this.isShownReceiptSharing = true;
    },
    handleAnnulledDeclarationShown() {
      this.isAnnulledDeclarationShown = true;
    },
    async handleReceiptCancelByCode(code) {
      this.loading = true;

      try {
        await this.SELF_EMPLOYED_RECEIPT_CANCEL({
          publicId: this.transactionId,
          reason: code,
        });

        await this.loadDeclarationReceipt();
        await this.loadQRCodeToReceipt();

        this.CHANGE_SELECTED_TRANSACTION_DECLARED(false);

        this.$alert({
          type: 'success',
          text: this.$t('check.annulled'),
        });
      } catch (error) {
        logrocket.logger(error);

        this.$alert({
          type: 'warning',
          text: this.$t('inn.not.exists'),
        });
      }

      this.loading = false;
    },
    ...mapActions([
      'DECLARATION_RECEIPT',
      'GET_RECEIPT_CANCEL_REASONS',
      'GET_SELF_EMPLOYED_QR_CODE',
      'SELF_EMPLOYED_RECEIPT_CANCEL',
    ]),
    ...mapMutations(['CHANGE_SELECTED_TRANSACTION_DECLARED', 'SET_MODAL_PARAMS']),
  },
};
</script>

<style lang="sass" scoped>
  .container-wrapper
    max-width: 68rem
    width: 100%
    margin-left: auto
    margin-right: auto

  .container-content
    width: 100%
    padding: 20px 0 50px

    +respond-to("small")
      width: 360px
      margin: 0 auto
      padding: 60px 0 20px

  .header
    display: flex
    margin-bottom: 22px
    padding: 0 10px

    +respond-to("small")
      margin-bottom: 30px

  .header-back-link
    width: 40px
    height: 40px
    margin-right: 7px
    flex: none
    border-radius: 50%
    background-image: url("~@/assets/img/arrow-left.svg")
    background-repeat: no-repeat
    background-position: center

    #{theme("eclipse")} &
      background-color: #cbe2b3

    #{theme("obsidian")} &
      background-color: #181722

    +respond-to("small-medium")
      margin-left: -60px

    +respond-to("small")
      width: 60px
      height: 60px
      margin-right: 20px

    &:hover
      background-color: #7CB342

  .header-title
    align-self: center
    font-size: 20px
    line-height: 24px

    span
      display: block

      +respond-to("small")
        display: inline

  .content
    +respond-to("small")
      padding: 0 20px

  .transfer-receipt
    width: 100%
    display: inline-block
    padding: 20px
    background: #181823

    +respond-to("small")
      border-radius: 10px

  .transfer-receipt-offline
    margin-bottom: 20px

  .transfer-receipt-image
    margin-bottom: 20px
    font-size: 0

    img
      width: 100%
      border-radius: 10px

  .transfer-receipt-copy
    position: relative
    background: rgba(124, 179, 66, 0.25)
    border: 1px solid rgba(255, 255, 255, 0.07)
    border-radius: 8px
    padding: 10px
    cursor: pointer

  .transfer-receipt-copy-label
    margin-bottom: 5px
    font-size: 14px
    line-height: 17px
    color: #fff
    opacity: .5

  .transfer-receipt-copy-link
    font-weight: 500
    font-size: 20px
    line-height: 24px
    color: #fff
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  .transfer-receipt-copy-icon
    width: 18px
    height: 18px
    position: absolute
    top: 10px
    right: 10px
    background-image: url("~@/assets/img/icons/icon-copy.svg")
    background-repeat: no-repeat
    background-position: center

  .button-send
    margin: 22px 0 28px

    button
      width: 300px
      display: block
      margin: 0 auto
      padding: 16px
      font-weight: 600
      font-size: 18px
      line-height: 18px
      border-radius: 2rem
      background-color: #7CB342
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      &:hover(:not):disabled
        box-shadow: inset 0 -0.24rem 0 rgba(0, 0, 0, 0.2), 0 0.24rem 0.24rem rgba(124, 179, 66, 0.2)

      &:disabled,
      &.disabled
        color: #6b6b76
        background-color: #3a3a49
        cursor: not-allowed

  .button-link-container
    display: flex
    flex-flow: row wrap
    justify-content: space-between
    margin-top: 22px

    a:last-child
      width: 100%

    a:not(:last-child)
      margin-bottom: 20px

    +respond-to("small")
      width: 100%
      justify-content: space-between

      a:last-child
        width: auto

      a:not(:last-child)
        margin-bottom: 0px

  .button-link
    width: 200px
    margin: 0 auto
    padding: 10px 20px
    text-align: center
    border-radius: 50px
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: #fff
    background: #27283C
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15)
    cursor: pointer
</style>
